import React, { useMemo } from "react";
import { Button, Grid, Paper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { USER_ROLES, UCH_CLINICS, CLINIC_FOR_REVIEW } from "static/constants";
import { toggleModal } from "app/store/transmission/slice/transmissionSlice";
import {
  ArchiveOutlined,
  ArrowBack,
  ArrowForward,
  AttachMoney,
  Download,
  PublishedWithChanges,
  Send,
  TaskAlt,
} from "@mui/icons-material";

const TransmissionProcess = ({ role }) => {
  const {
    reportList,
    isHl7JobModal,
    selectedTab,
    isProcessModal,
    isBillingModal,
    isSigningModal,
    isArchiveModal,
    isDownloadModal,
    selectedReports,
    totalSelectedRows,
  } = useSelector((state) => state.transmission);
  const { accountsMapper } = useSelector((state) => state.account);

  const clinicName = localStorage.getItem("clinicName");
  const physician = localStorage.getItem("physician") === "true";
  const clinicReviewer = localStorage.getItem("clinicReviewer") === "true";

  const dispatch = useDispatch();

  const reportListMap = useMemo(() => {
    return reportList.reduce((acc, report) => {
      acc[report._id] = report;
      return acc;
    }, {});
  }, [reportList]);

  const showExportToEhrButton = () => {
    return selectedReports.every((reportId) => {
      const { account, alert_flag, warning_flag, last_billing_date } =
        reportListMap[reportId] || {};

      const {
        settings: {
          sftp_account,
          hl7_configs: { oru, dft, allowed_tabs } = {},
        } = {},
      } = accountsMapper[account] || {};

      return (
        !sftp_account &&
        (oru || dft) &&
        allowed_tabs?.includes(selectedTab) &&
        (alert_flag || warning_flag || last_billing_date)
      );
    });
  };

  const showUnsignButton = (role) => {
    return selectedReports.every((reportId) => {
      const { eSig } = reportListMap[reportId] || {};

      return eSig && role !== USER_ROLES.ASSISTANT;
    });
  };

  const showProcessButton = () => {
    if (!UCH_CLINICS.includes(clinicName)) return true;
    if (selectedTab !== CLINIC_FOR_REVIEW) return true;

    return selectedReports.every((reportId) => {
      const { signedBy, eSig } = reportListMap[reportId] || {};

      return signedBy && eSig;
    });
  };

  return (
    <Grid container justifyContent="center" className="trans-table__process">
      {totalSelectedRows > 0 &&
        !isProcessModal &&
        !isBillingModal &&
        !isDownloadModal &&
        !isSigningModal &&
        !isArchiveModal &&
        !isHl7JobModal && (
          <Grid item>
            <Paper
              style={{ padding: "10px 12px", borderRadius: "4px" }}
              component={Grid}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                color="primary"
                variant="outlined"
                style={{
                  borderRadius: "50px",
                  fontSize: "14px",
                  lineHeight: "20px",
                  fontWeight: 400,
                  padding: "10px 15px !important",
                  color: "#757575",
                  borderColor: "#E0E0E0",
                }}
              >
                {totalSelectedRows} Selected
              </Button>

              <div>
                <Button
                  style={{
                    color: "#757575",
                  }}
                  variant="text"
                  onClick={() =>
                    dispatch(toggleModal({ type: "download", value: true }))
                  }
                  startIcon={<Download />}
                >
                  Download
                </Button>
                {[USER_ROLES.ADMIN, USER_ROLES.USER].includes(role) &&
                  selectedTab === "active" && (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() =>
                        dispatch(toggleModal({ type: "archive", value: true }))
                      }
                      style={{
                        color: "#757575",
                      }}
                      startIcon={<ArchiveOutlined />}
                    >
                      Archive
                    </Button>
                  )}
                {role === "clinic" &&
                  (clinicReviewer || physician) &&
                  clinicName !== "Denver Heart" && (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() =>
                        dispatch(toggleModal({ type: "sign", value: true }))
                      }
                      style={{
                        color: "#757575",
                      }}
                      startIcon={<TaskAlt />}
                    >
                      Sign
                    </Button>
                  )}

                {showUnsignButton(role) && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() =>
                      dispatch(toggleModal({ type: "unsign", value: true }))
                    }
                    style={{
                      color: "#757575",
                    }}
                  >
                    Unsign
                  </Button>
                )}

                {role === "clinic" &&
                  selectedTab !== "clinic-processed" &&
                  showProcessButton() && (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() =>
                        dispatch(toggleModal({ type: "process", value: true }))
                      }
                      style={{
                        color: "#757575",
                      }}
                      startIcon={<ArrowForward />}
                    >
                      Process
                    </Button>
                  )}

                {physician &&
                  clinicName !== "Denver Heart" &&
                  selectedTab !== "clinic-processed" && (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() =>
                        dispatch(
                          toggleModal({ type: "signAndProcess", value: true })
                        )
                      }
                      style={{
                        color: "#757575",
                      }}
                      startIcon={<PublishedWithChanges />}
                    >
                      Sign & Process
                    </Button>
                  )}

                {role === "clinic" && selectedTab === "clinic-processed" && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() =>
                      dispatch(toggleModal({ type: "billing", value: true }))
                    }
                    style={{
                      color: "#757575",
                    }}
                    startIcon={<AttachMoney />}
                  >
                    Bill
                  </Button>
                )}
                {role === "clinic" && selectedTab === "clinic-review" && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() =>
                      dispatch(toggleModal({ type: "unprocess", value: true }))
                    }
                    style={{
                      color: "#757575",
                    }}
                    startIcon={<ArrowBack />}
                  >
                    Un-Process
                  </Button>
                )}
                {[USER_ROLES.ADMIN, USER_ROLES.USER].includes(role) &&
                  (selectedTab === "active" || selectedTab === "processed") && (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() =>
                        dispatch(
                          toggleModal({
                            type:
                              selectedTab === "active"
                                ? "process"
                                : "unprocess",
                            value: true,
                          })
                        )
                      }
                      style={{
                        color: "#757575",
                      }}
                      startIcon={
                        selectedTab === "active" ? (
                          <ArrowForward />
                        ) : (
                          <ArrowBack />
                        )
                      }
                    >
                      {selectedTab === "active" ? "Process" : "Unprocess"}
                    </Button>
                  )}

                {[USER_ROLES.ADMIN, USER_ROLES.CLINIC].includes(role) && (
                  <>
                    {showExportToEhrButton() && (
                      <Button
                        variant="text"
                        color="inherit"
                        style={{
                          color: "#757575",
                        }}
                        onClick={() =>
                          dispatch(toggleModal({ type: "hl7job", value: true }))
                        }
                        startIcon={<Send />}
                      >
                        Export to EHR
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Paper>
          </Grid>
        )}
    </Grid>
  );
};

export default TransmissionProcess;
